import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { routeTree } from './routeTree.gen';
import './globals.css';
import { EmployeeProvider } from './shared/contexts/EmployeeContext';
import { LanguageProvider } from './shared/contexts/LanguageContext';
import { AuthProvider } from './shared/providers/AuthProvider';
import { DirectionProviderWrapper } from './shared/providers/DirectionProviderWrapper';

// Conditionally load react-scan in non-production environments
if (!import.meta.env.PROD) {
  const script = document.createElement('script');
  script.crossOrigin = 'anonymous';
  script.src = '//unpkg.com/react-scan/dist/auto.global.js';
  document.head.appendChild(script);
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

const stripUrlQuery = (value: string): string => {
  try {
    const url = new URL(value, window.location.origin);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split(/[?#]/, 1)[0] ?? value;
  }
};

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    sendDefaultPii: false,
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = stripUrlQuery(event.request.url);
      }
      return event;
    },
    beforeSendTransaction(event) {
      if (event.request?.url) {
        event.request.url = stripUrlQuery(event.request.url);
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'navigation' && breadcrumb.data) {
        for (const key of ['from', 'to']) {
          const value = breadcrumb.data[key];
          if (typeof value === 'string') {
            breadcrumb.data[key] = stripUrlQuery(value);
          }
        }
      }
      return breadcrumb;
    },
  });
}

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: false,
    sanitize_properties(properties) {
      for (const key of ['$current_url', '$referrer']) {
        const value = properties[key];
        if (typeof value === 'string') {
          properties[key] = stripUrlQuery(value);
        }
      }
      return properties;
    },
    defaults: '2025-05-24',
    capture_exceptions: {
      capture_console_errors: true,
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
    },
  });
} else {
  console.warn('PostHog key is missing. Analytics disabled.');
}

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (axios error format)
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { status: number } };
          const status = axiosError.response?.status;
          if (status && status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
    },
  },
});

const router = createRouter({ routeTree });
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            fontFamily: 'system-ui, sans-serif',
            gap: '16px',
          }}
        >
          {/* i18n not guaranteed to be ready at error-boundary render,
              so the fallback uses hardcoded English strings. */}
          <p style={{ fontSize: '18px', color: '#374151' }}>
            Something went wrong.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: '#f9fafb',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Reload page
          </button>
        </div>
      }
    >
      <PostHogProvider client={posthog}>
        <LanguageProvider>
          <AuthProvider>
            <QueryClientProvider client={queryClient}>
              <DirectionProviderWrapper>
                <EmployeeProvider>
                  <RouterProvider router={router} />
                </EmployeeProvider>
                {/* <ReactQueryDevtools initialIsOpen={false} /> */}
              </DirectionProviderWrapper>
            </QueryClientProvider>
          </AuthProvider>
        </LanguageProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
